import { CircleNotchIcon } from "@phosphor-icons/react";
import type { RefObject } from "react";
import ArticleCard from "@/components/ui/article-card/article-card";
import Checkbox from "@/components/ui/forms/checkbox";
import { useArticles } from "@/store/news-feed/feed-content-management/feed-content-management.selectors";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import ArticlesContentEmptyState from "./ArticlesContentEmptyState";
import { useArticlesPagination } from "./useArticlesPagination";

interface ArticlesContainerProps {
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Article list with infinite scroll.
 */
const ArticlesContainer = ({ scrollContainerRef }: ArticlesContainerProps) => {
  const articles = useArticles();
  const error = useFeedContentManagementStore((state) => state.error);
  const isInitialLoading = useFeedContentManagementStore(
    (state) => state.isInitialLoading,
  );
  const selectedArticleIds = useFeedContentManagementStore(
    (state) => state.selectedArticleIds,
  );
  const excludedArticleIds = useFeedContentManagementStore(
    (state) => state.excludedArticleIds,
  );
  const isAllSelected = useFeedContentManagementStore(
    (state) => state.isAllSelected,
  );
  const toggleArticleSelection = useFeedContentManagementStore(
    (state) => state.toggleArticleSelection,
  );
  const setSingleArticleIdForAction = useFeedContentManagementStore(
    (state) => state.setSingleArticleIdForAction,
  );
  const setDeleteModalOpen = useFeedContentManagementStore(
    (state) => state.setDeleteModalOpen,
  );
  const setEditModalOpen = useFeedContentManagementStore(
    (state) => state.setEditModalOpen,
  );
  const populateEditFieldsForSingleArticle = useFeedContentManagementStore(
    (state) => state.populateEditFieldsForSingleArticle,
  );
  const setRemoveArticleFromClusterModalOpen = useFeedContentManagementStore(
    (state) => state.setRemoveArticleFromClusterModalOpen,
  );
  const setMoveArticleFromNarrativeClusterModalOpen =
    useFeedContentManagementStore(
      (state) => state.setMoveArticleFromNarrativeClusterModalOpen,
    );
  const setClusterName = useNewsFeedFilters((state) => state.setClusterName);
  const setSelectedCluster = useNewsFeedFilters(
    (state) => state.setSelectedCluster,
  );

  const { loadMoreRef, pagination, isFetchingArticles } =
    useArticlesPagination(scrollContainerRef);

  const handleDeleteArticle = (articleId: string) => {
    setSingleArticleIdForAction(articleId);
    setDeleteModalOpen(true);
  };

  const handleEditArticle = (articleId: string) => {
    setSingleArticleIdForAction(articleId);
    populateEditFieldsForSingleArticle(articleId);
    setEditModalOpen(true);
  };

  const handleMoveToCluster = (
    articleId: string,
    storyId: string | null,
    storyName: string | null,
  ) => {
    setSingleArticleIdForAction(articleId);
    if (storyId && storyName) {
      setSelectedCluster({ id: storyId, name: storyName });
    } else {
      setSelectedCluster(null);
    }
    setMoveArticleFromNarrativeClusterModalOpen(true);
  };

  const handleRemoveFromCluster = (
    articleId: string,
    storyName: string | null,
  ) => {
    setSingleArticleIdForAction(articleId);
    setClusterName(storyName);
    setRemoveArticleFromClusterModalOpen(true);
  };

  if (error) {
    console.error("Error fetching articles:", error);
    return (
      <div className="text-sm text-red-500 pl-7 py-2">
        An error occurred while fetching the articles. Please try again later.
      </div>
    );
  }

  if (isInitialLoading && articles.length === 0) {
    const skeletonArticles = Array.from({ length: 3 }, (_, i) => ({
      id: `skeleton-${i}`,
    }));

    return (
      <div className="flex flex-col">
        <div className="space-y-4">
          {skeletonArticles.map((skeletonArticle) => (
            <div key={skeletonArticle.id} className="flex gap-3 items-start">
              <Checkbox
                checked={false}
                disabled
                aria-label="Loading article"
                className="mt-5"
              />
              <ArticleCard
                // biome-ignore lint/suspicious/noExplicitAny: Minimal article object for skeleton loading state
                article={skeletonArticle as any}
                isLoading={true}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0 && !isFetchingArticles) {
    return (
      <ArticlesContentEmptyState
        title="No items match your filters"
        body="Try adjusting your filters to find the news you're looking for."
      />
    );
  }

  return (
    <div className="flex flex-col pb-8">
      <div className="space-y-4">
        {articles.map(({ node: article }) => {
          const isSelected = isAllSelected
            ? !excludedArticleIds.has(article.id)
            : selectedArticleIds.has(article.id);

          return (
            <div key={article.id} className="flex gap-3 items-start">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleArticleSelection(article.id)}
                aria-label={`Select ${article.headline || "article"}`}
                className="mt-5"
              />

              <ArticleCard
                article={article}
                isSelected={isSelected}
                handleDeleteClick={() => handleDeleteArticle(article.id)}
                handleEditClick={() => handleEditArticle(article.id)}
                handleMoveToClusterClick={() => {
                  handleMoveToCluster(
                    article.id,
                    article.storyId ?? null,
                    article.storyName ?? null,
                  );
                }}
                handleRemoveFromClusterClick={() => {
                  handleRemoveFromCluster(
                    article.id,
                    article.storyName ?? null,
                  );
                }}
              />
            </div>
          );
        })}
      </div>

      {isFetchingArticles && (
        <div className="flex items-center justify-center gap-2 py-4">
          <CircleNotchIcon className="animate-spin h-4 w-4 text-blue-500" />
          <div className="text-gray-500 text-sm">Loading more articles</div>
        </div>
      )}

      {pagination.hasNextPage && <div ref={loadMoreRef} className="h-8" />}
    </div>
  );
};

export default ArticlesContainer;
